/* eslint-disable import/no-cycle */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import Overflow from 'rc-overflow';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import HorizontalScrollIndicators from 'components/uiLibrary/HorizontalScrollIndicators';
import LinkButton, { TextButton } from 'components/uiLibrary/LinkButton';
import Skeleton from 'components/uiLibrary/Loaders/Skeleton';
import Modal from 'components/uiLibrary/Modal';
import Popover from 'components/uiLibrary/Popover';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import CastingRoles from 'components/Casting/Display/CastingRoles';
import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, SECTIONS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import CommonTrans from 'components/Globals/CommonTrans';
import useQuickView, { QUICK_VIEW_TYPES } from 'components/Globals/Layout/QuickView';
import { useShowPageAccessPortal } from 'components/Globals/PortalsProvider';
import { useTrackingClickStyles } from 'components/Globals/TrackingTester';
import Loader from 'components/Loader';

import { CONTRIBUTORS, DATE_FORMATS, ENTITY_MAIN_TABS, PROFESSION_IDS, SORT_OPTION_TYPES, TP } from 'constants/index';

import { getBooleanSearchQuery } from 'utils/casting';
import { createDate } from 'utils/date';
import { transformAppliedFilters } from 'utils/globals/filters';
import useAppContext from 'utils/hooks/useAppContext';
import useArchiveLoginModal from 'utils/hooks/useArchiveLoginModal';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useDialogs } from 'utils/hooks/useDialogs';
import useGetFilterTypes from 'utils/hooks/useGetFilterTypes';
import useNavigate from 'utils/hooks/useNavigate';
import usePageContext from 'utils/hooks/usePageContext';
import useScrollBlocker from 'utils/hooks/useScrollBlocker';
import useScrollIntoView from 'utils/hooks/useScrollIntoView';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Productions/queries';

import { useTranslation } from 'src/i18n';

import {
  AGGREGATION_TYPES,
  ENTITY_TYPES,
  FILTER_GROUP,
  FILTER_LABELS,
  FILTER_TYPES,
  ROUTE_RESERVED_KEYWORDS,
} from 'operabase-router/constants';

import FilterGroupButton from '../FilterGroupButton';
import FilterOptions from '../FilterOptions';

import classes from './FilterFacets.module.scss';

const filterTrackingData = {
  section: SECTIONS.QUICK_FILTERS,
};

export const LAYOUT = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
  QUICK_VIEW: 'quickView',
  BUTTON: 'button',
  BUTTON_GROUP_DETAILED: 'button_group_detailed',
  ICON: 'ICON',
  TEXT: 'text',
};

const EXPANDABLE_FILTERS = [
  FILTER_TYPES.PRODUCER,
  CONTRIBUTORS.CREW,
  CONTRIBUTORS.CAST,
  CONTRIBUTORS.INSTRUMENTALIST,
  CONTRIBUTORS.ENSEMBLE,
];

const useGetDynamicFiltersProps = () => {
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext;
  const { filters } = linkProps || {};

  const appliedFiltersStats = useMemo(() => {
    let appliedFilters = {};
    const contributionExp = [...(filters?.[FILTER_TYPES.ROLE] || []), ...(filters?.[FILTER_TYPES.ENSEMBLE] || [])];
    contributionExp.forEach(({ entity, contributors, parents, entityType }) => {
      const key = entity?.slug;

      if (key) {
        const contributorCount = contributors?.length || 0;
        appliedFilters[key] = contributorCount;

        // NOTE: Count roles by parent type in the same iteration
        if (entityType === ENTITY_TYPES.PROFESSION) {
          const parentId = parents?.[0]?.entity?.id;

          if (!parentId || !parents?.length) {
            appliedFilters[CONTRIBUTORS.CREW] = (appliedFilters[CONTRIBUTORS.CREW] || 0) + contributorCount;
          } else if (parentId === PROFESSION_IDS.INSTRUMENTATION) {
            appliedFilters[CONTRIBUTORS.INSTRUMENTALIST] =
              (appliedFilters[CONTRIBUTORS.INSTRUMENTALIST] || 0) + contributorCount;
          } else if (parentId === PROFESSION_IDS.SINGER) {
            appliedFilters[CONTRIBUTORS.CAST] = (appliedFilters[CONTRIBUTORS.CAST] || 0) + contributorCount;
          }
        }

        if (entityType === ENTITY_TYPES.ENSEMBLE_TYPE) {
          appliedFilters[CONTRIBUTORS.ENSEMBLE] = (appliedFilters[CONTRIBUTORS.ENSEMBLE] || 0) + contributorCount;
        }
      }
    });

    if (filters?.[FILTER_TYPES.PRODUCER]?.length) {
      const appliedProducerFilters = filters?.[FILTER_TYPES.PRODUCER];
      appliedFilters[FILTER_TYPES.PRODUCER] = appliedProducerFilters?.length || 0;
      appliedFilters = {
        ...appliedFilters,
        ...appliedProducerFilters?.reduce((acc, { entity, contributors }) => {
          acc[entity?.slug] = contributors?.length || 0;
          return acc;
        }, {}),
      };
    }

    const primaryFilters = [
      FILTER_TYPES.CONDUCTOR,
      FILTER_TYPES.DIRECTOR,
      FILTER_TYPES.CO_PRODUCER,
      FILTER_TYPES.VENUE,
      FILTER_TYPES.CHOREOGRAPHER,
    ];
    primaryFilters.forEach(filterType => {
      if (filters?.[filterType]?.length) {
        appliedFilters[filterType] = filters?.[filterType]?.length || 0;
      }
    });
    return appliedFilters;
  }, [filters]);

  return appliedFiltersStats;
};

function FilterChip({
  type,
  count,
  label,
  appliedCount,
  onClick,
  active,
  disable,
  inline,
  isExpanded,
  hideChevron,
  trackingClasses = '',
  styles,
}) {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const ref = useRef(null);
  const isDisabled = !!disable || count === 0;
  const value = useMemo(() => {
    if ([FILTER_TYPES.CAST, FILTER_TYPES.CREW, FILTER_TYPES.INSTRUMENTALIST, FILTER_TYPES.ENSEMBLE].includes(label)) {
      return t(FILTER_LABELS[label]);
    }

    if (label) {
      return label;
    }

    return t(FILTER_LABELS[type]);
  }, [label, t, type]);

  const onClickHandler = e => {
    e.stopPropagation();
    e.preventDefault();

    if (count === 0) {
      return;
    }

    onClick({ anchor: ref?.current, type });
  };

  return (
    <div
      className={classnames(classes.chip, trackingClasses, {
        [classes.inlineChip]: !!inline,
        [classes.active]: active,
        [classes.disabled]: isDisabled,
        [classes.applied]: appliedCount > 0,
        [styles?.root]: !!styles?.root,
      })}
      ref={ref}
      onClick={onClickHandler}
    >
      <Typography
        variant={inline ? 'h3' : 'span'}
        size={12}
        className={classes.chip__label}
        weight={isExpanded ? 'medium' : 'regular'}
        color={isDisabled ? 'tertiary' : 'primary'}
        truncate
      >
        {inline ? (
          <CommonTrans ns="NS_APP_GLOBALS" i18nKey={t(`${TP}.FN_FILTER_BY_OPTION`, { option: value })} />
        ) : (
          value
        )}
      </Typography>
      {!hideChevron && <SpriteIcon className={classes.chip__icon} icon={isExpanded ? 'expand_less' : 'expand_more'} />}
      {!isDisabled && appliedCount > 0 && (
        <Typography variant="span" size={12} className={classes.chip__applied} color="white">
          {appliedCount}
        </Typography>
      )}
      {!inline && typeof count === 'number' && (
        <Typography size={12} className={classes.chip__count} color={isDisabled ? 'tertiary' : 'secondary'}>
          {count}
        </Typography>
      )}

      {disable && <SpriteIcon className={classes.chip__lock} icon="lock" size={14} />}
    </div>
  );
}

function ExtraFilters({ count, onClick }) {
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <div className={classes.chip} onClick={() => onClick({})}>
      {t(`${TP}.FN_PLUS_X_MORE_NO_BRACKET`, { number: count })}
      <SpriteIcon className={classes.chip_icon} icon="expand_more" />
    </div>
  );
}

function OverflowItemWrapper({ inline, children }) {
  if (inline) {
    return <div className={classes.overflowItemWrapper}>{children}</div>;
  }

  return children;
}

function ContributionExpressionFilterChip({
  key,
  item,
  appliedFiltersStats,
  isActive,
  onClick,
  trackingClasses,
  trackingData,
  enableScroll = false,
  inline,
  hideChevron,
  styles,
  isExpanded,
  disable,
}) {
  const { ref: scrollRef } = useScrollIntoView({
    enabled: enableScroll,
  });
  return (
    <div ref={scrollRef} key={`${item?.id}-${key || item?.slug}`}>
      <FilterChip
        label={item?.label}
        type={item?.type}
        active={isActive}
        count={item?.contribCount || item?.total}
        appliedCount={appliedFiltersStats?.[item?.slug] || 0}
        onClick={onClick}
        trackingData={trackingData}
        trackingClasses={trackingClasses}
        inline={inline}
        hideChevron={hideChevron}
        styles={styles}
        isExpanded={isExpanded}
        disable={disable}
      />
    </div>
  );
}

const getFilterProps = ({ type, filters, stats, hideFilterCounts, disabledTypes }) => {
  // NOTE: Early return for tickets watch online case
  if (type === FILTER_TYPES.TICKETS_WATCH_ONLINE) {
    // NOTE: Helper function to calculate count for tickets and watch online
    const getTicketsWatchOnlineCount = () => {
      if (stats?.[FILTER_TYPES.TICKET] > 0 && stats?.[FILTER_TYPES.WATCH_ONLINE] > 0) {
        return 2;
      }

      if (stats?.[FILTER_TYPES.TICKET] > 0 || stats?.[FILTER_TYPES.WATCH_ONLINE] > 0) {
        return 1;
      }

      return null;
    };

    const count = getTicketsWatchOnlineCount(stats);

    return {
      appliedCount: !!filters?.[FILTER_TYPES.TICKET] || !!filters?.[FILTER_TYPES.WATCH_ONLINE] ? 1 : 0,
      count: hideFilterCounts ? null : count,
      disable: disabledTypes.includes(type) || count === null,
      total: count,
    };
  }

  // NOTE: Early return for cover debut case
  if (type === FILTER_TYPES.COVER_DEBUT && !hideFilterCounts) {
    const count =
      (stats?.[FILTER_TYPES.COVER_ROLE] || 0) +
      (stats?.[FILTER_TYPES.ROLE_DEBUT] || 0) +
      (stats?.[FILTER_TYPES.PRODUCER_DEBUT] || 0);
    return {
      appliedCount: filters?.[type]?.length || 0,
      count,
      disable: disabledTypes.includes(type) || count === 0,
      total: count,
    };
  }

  if (type === FILTER_TYPES.COUNTRY) {
    return {
      appliedCount: filters?.[type]?.length || filters?.[FILTER_TYPES.CITY]?.length || 0,
      count: hideFilterCounts ? null : stats?.[type],
      disable: disabledTypes.includes(type) || stats?.[type] === 0,
      total: stats?.[type],
    };
  }

  return {
    appliedCount: filters?.[type]?.length || 0,
    count: hideFilterCounts ? null : stats?.[type],
    disable: disabledTypes.includes(type) || stats?.[type] === 0,
    total: stats?.[type],
  };
};

function RenderFacets({
  stats,
  subFilterType,
  expandedFilter,
  onExpandFilter,
  disable = false,
  inline = false,
  upcoming = false,
  disabledTypes,
  hideChevron = false,
  hideFilterCounts = false,
  scrollable,
  trackingData = {},
  commonFilterParams,
  filterGroup,
  filterOptionGroup,
  setFilterOptionGroup,
}) {
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext;
  const { filters } = linkProps;
  const { entityType } = usePageContext();
  const trackingClasses = useTrackingClickStyles(trackingData, false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [activeContributionType, setActiveContributionType] = useState(null);

  const filtersParams = commonFilterParams;
  const appliedFiltersStats = useGetDynamicFiltersProps({
    filtersParams,
  });

  const queryFn = queries.getDynamicFiltersForCastingTool;
  const { data: contributionTypes, isFetching: isLoadingDynamicFilters } = useQuery(
    queryFn({
      filters: filtersParams,
    }),
  );

  // NOTE: Process dynamic filters and find scrollable item (moved from useGetDynamicFilterOptions)
  const { processedContributionTypes, scrollableItem } = useMemo(() => {
    // NOTE: If data is still loading, find scrollable item if needed
    const scrollItem =
      !!subFilterType?.scrollTo && contributionTypes?.flat()?.find(item => item?.slug === subFilterType?.scrollTo);

    return {
      processedContributionTypes: contributionTypes,
      scrollableItem: scrollItem || null,
    };
  }, [contributionTypes, subFilterType]);

  // NOTE: Helper function to handle scrollable item selection
  const handleScrollableItemSelection = useCallback(
    item => {
      if ([CONTRIBUTORS.INSTRUMENTALIST, FILTER_TYPES.PRODUCER].includes(item?.slug)) {
        setExpandedItem({ ...item, isExpandable: true });

        if (item?.slug === FILTER_TYPES.PRODUCER) {
          onExpandFilter({ type: FILTER_TYPES.CONTRIBUTION_EXPRESSION, selectedSubFilterType: item });
          setActiveContributionType(item);
        }

        return;
      }

      onExpandFilter({ type: FILTER_TYPES.CONTRIBUTION_EXPRESSION, selectedSubFilterType: item });
      setActiveContributionType(item);
    },
    [onExpandFilter],
  );

  // NOTE: Helper function to handle subFilterType selection for quick view
  const handleSubFilterTypeSelection = useCallback(() => {
    // NOTE: subFilterType already has all necessary properties from inline click
    if (!EXPANDABLE_FILTERS.includes(subFilterType?.slug)) {
      setActiveContributionType(subFilterType);
    }

    const isExpandable = EXPANDABLE_FILTERS.includes(subFilterType?.parent?.slug || subFilterType?.slug);

    if (isExpandable) {
      setExpandedItem(subFilterType?.parent || subFilterType);
    }
  }, [subFilterType]);

  // NOTE: Handle scroll and subFilterType selection
  useEffect(() => {
    if (!contributionTypes?.length) {
      return;
    }

    // NOTE: Handle scroll to specific item
    if (subFilterType?.scrollTo && scrollableItem) {
      handleScrollableItemSelection(scrollableItem);
      return;
    }

    // NOTE: Handle subFilterType selection for quick view
    if (expandedFilter === FILTER_TYPES.CONTRIBUTION_EXPRESSION && subFilterType && !subFilterType.scrollTo) {
      handleSubFilterTypeSelection();
    }
  }, [
    contributionTypes,
    processedContributionTypes,
    scrollableItem,
    subFilterType,
    expandedFilter,
    handleScrollableItemSelection,
    handleSubFilterTypeSelection,
  ]);

  // NOTE: Fetch sub-filters when an item is expanded
  const { data: subFilters, isFetching: isLoadingSubFilters } = useQuery(
    queries.getAggregatedEntities({
      filters: {
        ...filtersParams,
        ...expandedItem?.subFilterApiParams,
        sort: SORT_OPTION_TYPES.PROD_COUNT_DESC.value,
        include_contrib_count: true,
      },
      aggregationType: expandedItem?.aggregationType,
      queryConfig: {
        enabled: expandedItem?.slug === FILTER_TYPES.PRODUCER,
        select: data => ({
          data: data?.data?.map(option => ({
            ...option,
            label: option?.name,
            slug: option?.slug || option?.id,
            filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
            type: expandedItem?.type,
          })),
        }),
      },
    }),
  );

  const subFiltersList = useMemo(() => {
    if (expandedItem?.slug === FILTER_TYPES.PRODUCER && subFilters?.data?.length) {
      return subFilters?.data;
    }

    if (
      [CONTRIBUTORS.CREW, CONTRIBUTORS.CAST, CONTRIBUTORS.INSTRUMENTALIST, CONTRIBUTORS.ENSEMBLE].includes(
        expandedItem?.slug,
      )
    ) {
      return (() => {
        const targetGroup = processedContributionTypes?.flat()?.find(item => item.slug === expandedItem?.slug);

        if (!targetGroup?.data) {
          return [];
        }

        return targetGroup.data
          .map(option => ({
            ...option,
            label: option.name,
            slug: option.slug || option.id,
            filterType: FILTER_TYPES.CONTRIBUTION_EXPRESSION,
            type: expandedItem?.type,
          }))
          .sort((a, b) => (b?.contribCount || 0) - (a?.contribCount || 0));
      })();
    }

    return [];
  }, [expandedItem, processedContributionTypes, subFilters?.data]);

  // NOTE: Reset expanded item when filter changes
  useEffect(() => {
    if (!subFilterType?.parent?.slug && !EXPANDABLE_FILTERS.includes(subFilterType?.slug)) {
      setExpandedItem(null);
    }
  }, [subFilterType]);

  // NOTE: Auto-select first sub-filter for non-producer expandable items
  useEffect(() => {
    if (onExpandFilter && subFiltersList?.length && ![FILTER_TYPES.PRODUCER].includes(expandedItem?.slug)) {
      setActiveContributionType(subFiltersList[0]);
      onExpandFilter({
        type: expandedItem?.filterType,
        selectedSubFilterType: {
          ...subFiltersList[0],
          parent: expandedItem,
        },
      });
    }
  }, [expandedItem, subFiltersList]);

  // NOTE: Process dynamic filters into optimized lookup maps
  const { dynamicFiltersMap, dynamicFiltersByType } = useMemo(() => {
    const filtersMap = {};
    const filtersByType = {};

    if (processedContributionTypes && !isLoadingDynamicFilters) {
      processedContributionTypes.flat().forEach(filter => {
        // NOTE: Create enriched filter object once
        const enrichedFilter = {
          ...filter,
          isDynamic: true,
          appliedCount: appliedFiltersStats?.[filter?.slug] || 0,
          count: filter?.contribCount || filter?.total,
          active: false,
          disable: false,
        };

        // NOTE: Key by slug for uniqueness
        filtersMap[filter.slug] = enrichedFilter;

        // NOTE: Group by type for type-based lookups
        if (!filtersByType[filter.type]) {
          filtersByType[filter.type] = [];
        }

        filtersByType[filter.type].push(enrichedFilter);
      });
    }

    return { dynamicFiltersMap: filtersMap, dynamicFiltersByType: filtersByType };
  }, [processedContributionTypes, isLoadingDynamicFilters, appliedFiltersStats]);

  // NOTE: Create filtered and sorted filter groups
  const mergedFilterGroups = useMemo(
    () =>
      filterGroup?.map(subArray => {
        const filteredSubArray = subArray.filter(type => !disabledTypes?.includes(type));

        const filteredArray = filteredSubArray
          .map(type => {
            // NOTE: Check if this is a dynamic filter reference by type
            if (dynamicFiltersByType[type]) {
              return dynamicFiltersByType[type].map(filter => ({
                ...filter,
                active: expandedFilter === type && (!subFilterType || subFilterType?.slug === filter.slug),
              }));
            }

            // NOTE: Handle the old CONTRIBUTION_EXPRESSION type - render all remaining dynamic filters
            if (type === FILTER_TYPES.CONTRIBUTION_EXPRESSION) {
              // NOTE: Return all dynamic filters that weren't already rendered individually
              const alreadyRendered = new Set();

              filterGroup.flat().forEach(filterType => {
                if (dynamicFiltersByType[filterType]) {
                  dynamicFiltersByType[filterType].forEach(filter => {
                    alreadyRendered.add(filter.slug);
                  });
                }
              });

              return Object.values(dynamicFiltersMap)
                .filter(filter => !alreadyRendered.has(filter.slug))
                .map(filter => ({
                  ...filter,
                  active:
                    expandedFilter === FILTER_TYPES.CONTRIBUTION_EXPRESSION &&
                    (!subFilterType || subFilterType?.slug === filter.slug),
                }));
            }

            // NOTE: Handle regular hard-coded filters
            const {
              appliedCount,
              count,
              disable: disableFilter,
              total,
            } = getFilterProps({
              type,
              filters,
              stats,
              hideFilterCounts,
              disabledTypes,
            });

            return {
              type,
              appliedCount,
              count,
              active: expandedFilter === type,
              disable: disableFilter,
              isDynamic: false,
              total,
            };
          })
          .flat() // NOTE: Flatten in case arrays were returned
          .sort((filterA, filterB) => (inline ? filterB.appliedCount - filterA.appliedCount : 0));

        if (disable) {
          return filteredArray;
        }

        return filteredArray.filter(item => {
          if (item?.isDynamic) {
            return (item?.contribCount || item?.total) > 0;
          }

          return item?.disable || (item?.total && item?.total > 0);
        });
      }),
    [
      filterGroup,
      disabledTypes,
      entityType,
      dynamicFiltersByType,
      filters,
      stats,
      hideFilterCounts,
      expandedFilter,
      subFilterType,
      dynamicFiltersMap,
      inline,
      disable,
    ],
  );

  // NOTE: Handle expandable filter clicks
  const onClickHandler = useCallback(
    item => {
      const isExpandable = EXPANDABLE_FILTERS.includes(item?.slug);

      if (isExpandable && item?.slug !== expandedItem?.slug) {
        setExpandedItem({ ...item, isExpandable: true });
      } else if (isExpandable) {
        setExpandedItem(null);
      }

      const [_producer, ...rest] = EXPANDABLE_FILTERS;

      if (onExpandFilter && (inline || !rest.includes(item?.slug))) {
        setActiveContributionType(item);
        onExpandFilter({
          type: item?.filterType,
          selectedSubFilterType: item,
        });
      }
    },
    [expandedItem?.slug, inline, onExpandFilter],
  );

  // NOTE: Handle sub-filter selection
  const onSubFilterSelected = useCallback(
    item => {
      if (onExpandFilter) {
        if (activeContributionType?.slug === FILTER_TYPES.PRODUCER) {
          setFilterOptionGroup({
            ...item,
            aggregationType: AGGREGATION_TYPES[FILTER_TYPES.PRODUCER],
          });
          return;
        }

        setActiveContributionType(item);
        onExpandFilter({
          type: item?.filterType,
          selectedSubFilterType: { ...item, parent: activeContributionType?.parent },
        });
      }
    },
    [onExpandFilter, activeContributionType?.slug, activeContributionType?.parent],
  );

  const Wrapper = scrollable ? HorizontalScroller : 'div';
  return (
    <Wrapper
      className={classnames(classes.renderFacets, {
        [classes.renderFacets__inline]: inline,
      })}
      {...(scrollable && { hideArrow: true })}
    >
      <Overflow
        className={classnames(classes.typeList, { [classes.inline]: inline })}
        data={mergedFilterGroups}
        renderItem={group => {
          // NOTE: Show skeleton for loading dynamic filters
          if (isLoadingDynamicFilters && !scrollable) {
            return Array.from({ length: 3 }, (_, index) => (
              <OverflowItemWrapper inline={inline} key={`skeleton-${index}`}>
                <Skeleton width="100%" height={37} />
                {index === 2 && !inline && <div className={classes.separator} />}
              </OverflowItemWrapper>
            ));
          }

          return group?.map((item, index) => {
            // NOTE: Handle dynamic filters
            if (item?.isDynamic) {
              const isExpandable = EXPANDABLE_FILTERS.includes(item?.slug);
              const isItemActive =
                expandedItem?.slug === item?.slug ||
                (activeContributionType?.slug === item?.slug && activeContributionType?.type === item?.type);
              const isItemExpanded = !inline && isExpandable && expandedItem?.slug === item?.slug;

              return (
                <OverflowItemWrapper inline={inline} key={`${item?.slug}-${index}`}>
                  <ContributionExpressionFilterChip
                    item={item}
                    appliedFiltersStats={appliedFiltersStats}
                    isActive={isItemActive}
                    onClick={() => onClickHandler({ ...item, isExpandable })}
                    trackingClasses={trackingClasses}
                    trackingData={trackingData}
                    inline={inline}
                    hideChevron={!isExpandable}
                    isExpanded={isItemExpanded}
                    enableScroll={scrollableItem?.id === item?.id && item?.type === scrollableItem?.type}
                    disable={disable}
                  />
                  {/* NOTE: Show loading state for sub-filters */}
                  {isLoadingSubFilters && isItemExpanded && <Loader className={classes.loader} small />}
                  {/* NOTE: Render sub-filters when expanded */}
                  {isItemExpanded &&
                    subFiltersList?.map((subFilter, pointer) => (
                      <ContributionExpressionFilterChip
                        appliedFiltersStats={appliedFiltersStats}
                        key={pointer}
                        item={subFilter}
                        isActive={
                          subFilter?.slug === filterOptionGroup?.slug ||
                          subFilter?.slug === activeContributionType?.slug
                        }
                        onClick={() => onSubFilterSelected(subFilter)}
                        trackingData={trackingData}
                        trackingClasses={trackingClasses}
                        styles={{
                          root: classes.expandableListItem,
                        }}
                        hideChevron
                        inline={inline}
                        disable={disable}
                      />
                    ))}
                  {index === group.length - 1 && !inline && <div className={classes.separator} />}
                </OverflowItemWrapper>
              );
            }

            // NOTE: Handle regular hard-coded filters
            return (
              <OverflowItemWrapper inline={inline} key={`${item?.type}-${index}`}>
                <FilterChip
                  key={item?.type}
                  type={item?.type}
                  active={item?.active}
                  count={item?.count}
                  appliedCount={item?.appliedCount}
                  onClick={onExpandFilter}
                  disable={disable}
                  inline={inline}
                  upcoming={upcoming}
                  hideChevron={hideChevron}
                  trackingData={trackingData}
                  trackingClasses={trackingClasses}
                />
                {index === group.length - 1 && !inline && <div className={classes.separator} />}
              </OverflowItemWrapper>
            );
          });
        }}
        renderRest={items => <ExtraFilters count={items?.length} onClick={onExpandFilter} />}
        maxCount={scrollable ? null : 'responsive'}
      />
    </Wrapper>
  );
}

function FilterModalHeader({ mainPath, upcoming, hasFiltersApplied, onResetFilters, onClose }) {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const title = useMemo(() => {
    let baseTitle = t(`${TP}.FILTERS`);

    if (mainPath === ENTITY_MAIN_TABS.PERFORMANCES) {
      baseTitle += upcoming ? ` (${t(`${TP}.FN_UPCOMING`)})` : ` (${t(`${TP}.FN_PAST`)})`;
    }

    return baseTitle;
  }, [t, mainPath, upcoming]);

  return (
    <div className={classes.header}>
      <div className={classes.header__label}>
        <div className={classes.header__icon}>
          <SpriteIcon icon="filter_list" />
        </div>
        <Typography weight="medium">{title}</Typography>
        {hasFiltersApplied && <SpriteIcon icon="replay-bold" onClick={onResetFilters} />}
      </div>
      <SpriteIcon className={classes.closeIcon} icon="close" onClick={onClose} />
    </div>
  );
}

function FilterModalContent({
  stats,
  appliedFilters,
  expandedFilter,
  onAccessArchives,
  onUpdateFilters,
  onExpandFilter,
  onClose,
  upcoming,
  disable,
  disabledTypes,
  trackingData,
  fullHeight = false,
  isSticky = false,
  subFilterType,
  filterGroup,
  hasFiltersApplied = false,
}) {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext;
  const { filters } = linkProps;
  const { entity } = usePageContext();
  const [filterOptionGroup, setFilterOptionGroup] = useState(null);

  const { showHeader, total } = useMemo(() => {
    const searchQuery = getBooleanSearchQuery(filters?.expression);

    const filterQuery = searchQuery?.filter(q => q.type === 'QUERY');
    let filterTotal = 0;

    if (FILTER_TYPES.TICKETS_WATCH_ONLINE === expandedFilter) {
      filterTotal = (stats?.[FILTER_TYPES.TICKET] || 0) + (stats?.[FILTER_TYPES.WATCH_ONLINE] || 0);
    } else {
      filterTotal =
        expandedFilter === FILTER_TYPES.CONTRIBUTION_EXPRESSION
          ? subFilterType?.contribCount || stats?.total
          : stats?.[expandedFilter];
    }

    return {
      showHeader: filterQuery?.length > 0 || entity?.name,
      total: filterTotal,
    };
  }, [filters?.expression, expandedFilter, entity?.name, stats, subFilterType?.contribCount]);

  return (
    <>
      <div
        className={classnames(classes.filterModal__header, {
          [classes.filterModal__header_isSticky]: isSticky,
        })}
      >
        <div className={classes.filterModal__header_filterText}>
          <SpriteIcon icon="filter_list" className={classes.icon} />
          <Typography weight="bold" className={classes.text}>
            {t(`${TP}.FILTERS`)}
          </Typography>
        </div>
        <div className={classes.filterModal__header_close} onClick={onClose}>
          <LinkButton
            variant="text"
            styles={{
              root: classes.closeButton,
            }}
            leftIcon={<SpriteIcon icon="modal_close" size={16} className={classes.icon} />}
            onClick={onClose}
            disableUnderline
            trackingData={{
              ...trackingData,
              component: COMPONENTS.CLOSE_CTA,
            }}
          >
            <Typography size="12" weight="medium" className={classes.cancelText}>
              {t(`${TP}.FN_CLOSE`)}
            </Typography>
          </LinkButton>
        </div>
      </div>
      {showHeader && (
        <div
          className={classnames(classes.filterModal__selectedInfo, {
            [classes.filterModal__selectedInfo_isSticky]: isSticky,
            [classes.filterModal__selectedInfo_isSticky_hasMultiRole]: isSticky,
          })}
        >
          {entity?.name ? (
            <Typography weight="bold">{entity.name}</Typography>
          ) : (
            <HorizontalScrollIndicators>
              <CastingRoles isFilterHeader />
            </HorizontalScrollIndicators>
          )}
        </div>
      )}
      <div
        className={classnames(classes.filterModal__content, { [classes.filterModal__content_fullHeight]: fullHeight })}
      >
        <div className={classes.filterModal__content_left}>
          <div className={classes.facets}>
            <Typography weight="bold" className={classes.title}>
              {t(`${TP}.FILTERS`)}
            </Typography>
            <RenderFacets
              filterGroup={filterGroup}
              stats={stats}
              subFilterType={subFilterType}
              commonFilterParams={appliedFilters}
              onExpandFilter={onExpandFilter}
              disable={disable}
              expandedFilter={expandedFilter}
              disabledTypes={disabledTypes}
              upcoming={upcoming}
              trackingData={trackingData}
              filterOptionGroup={filterOptionGroup}
              setFilterOptionGroup={setFilterOptionGroup}
              hideChevron
              scrollable
            />
          </div>
        </div>
        <div className={classes.filterModal__content_right}>
          {expandedFilter?.trim()?.length > 0 && !disable && (
            <FilterOptions
              selectedFilterType={expandedFilter}
              total={total}
              stats={stats}
              filterParams={appliedFilters}
              onUpdateFilters={onUpdateFilters}
              onAccessArchives={onAccessArchives}
              asPopover={false}
              upcoming={upcoming}
              trackingData={trackingData}
              subFilterType={subFilterType}
              filterOptionGroup={filterOptionGroup}
              onUpdateFilterOptionGroup={setFilterOptionGroup}
              hasFiltersApplied={hasFiltersApplied}
              inline
            />
          )}
        </div>
      </div>
    </>
  );
}

function FilterFacets({
  mainPath,
  disable: isDisable = false,
  commonFilterParams = {},
  disabledTypes = [],
  clearExcludedTypes = [],
  upcoming = false,
  layout = LAYOUT.VERTICAL,
  scrollable = false,
  defaultExpandedFilter,
  openInQuickView = true,
  trackingData = {},
  onCloseQuickView,
  customGroup = {},
  styles = {},
  ignoreSmallScreenGroup = false,
  defaultSubFilterType,
  truncateInOneLine,
  viewType,
}) {
  const { isMobile } = useDeviceTypeLayouts();
  const { obRouteContext } = useAppContext();
  const { linkProps, filterParams, basePath } = obRouteContext;
  const { filters } = linkProps;
  const { showQuickView, quickViewProps } = useQuickView();
  const { paywall, isLoggedIn } = useAppContext();
  const { setIsLoginDialog } = useDialogs();
  const { permissions } = usePageContext();
  const showPageAccessPortal = useShowPageAccessPortal();
  const { castingToolPermissions, isAdmin } = permissions;
  const hasCastingToolAccess = isAdmin || castingToolPermissions?.hasAccess;
  const disable = isDisable || !paywall?.allowFilters;

  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const {
    entityType,
    entity,
    applyFilters,
    resetFilters,
    filterTypeUsage,
    mainPath: currentMainPath,
  } = usePageContext();
  const track = useTracking();
  const { staticFilterTypes: types, excludeFilterTypes } = useGetFilterTypes(clearExcludedTypes);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [subFilterType, setSubFilterType] = useState(defaultSubFilterType);
  const filterGroup = useMemo(() => {
    if (linkProps?.pro) {
      if (!entity?.id) {
        return FILTER_GROUP.CASTING_TOOL;
      }

      return FILTER_GROUP.ENTITY_PRO_VIEW;
    }

    if ([ROUTE_RESERVED_KEYWORDS.artists, ROUTE_RESERVED_KEYWORDS.organisations].includes(basePath)) {
      return FILTER_GROUP.ENTITY_INDEX_PAGE;
    }

    if (LAYOUT.HORIZONTAL === layout) {
      return FILTER_GROUP.PUBLIC_VIEW_HORIZONTAL_FILTERS;
    }

    return FILTER_GROUP.DEFAULT;
  }, [basePath, entity?.id, layout, linkProps?.pro]);

  const [expandedFilter, setExpandedFilter] = useState(() => {
    if (filterTypeUsage?.limitExceeded) {
      return filterTypeUsage?.focusFilterType || types[0];
    }

    if (layout === LAYOUT.QUICK_VIEW) {
      return defaultExpandedFilter || filterGroup[0][0];
    }

    return null;
  });

  const isInlineLayout = layout === LAYOUT.HORIZONTAL;

  const openArchiveAccessModal = useArchiveLoginModal();
  useScrollBlocker(expandedFilter);

  const { baseAppliedFilters, facetsApiFilters } = useMemo(() => {
    const sinceYear = filterParams?.[FILTER_TYPES.SINCE_YEAR];

    const params = {
      ...filterParams,
      ...(sinceYear && {
        date_from: createDate(`${sinceYear}`).format(DATE_FORMATS.FULL_DATE),
      }),
      ...commonFilterParams,
      ...(entityType === ENTITY_TYPES.PROFILE && {
        include_virtual_contributors: true,
      }),
    };
    return {
      baseAppliedFilters: params,
      facetsApiFilters: {
        ...omit(params, [FILTER_TYPES.SINCE_YEAR]),
      },
    };
  }, [entityType, filterParams, commonFilterParams]);

  const isStubOverviewTab =
    [ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType) &&
    !currentMainPath &&
    [LAYOUT.HORIZONTAL, LAYOUT.BUTTON].includes(layout);

  const { data: stats } = useQuery(
    queries.getFilterCounts({
      types,
      filters: facetsApiFilters,
      extraQueryKeys: [types],
      queryConfig: {
        enabled: !disable && types?.length > 0,
        keepPreviousData: true,
      },
    }),
  );

  const { validFilters, hasFiltersApplied } = useMemo(() => {
    const validParams = omit(filters, [...(excludeFilterTypes || []), FILTER_TYPES.PAGE, FILTER_TYPES.QUERY]);
    return {
      hasFiltersApplied: validParams.length > 0,
      validFilters: Object.values(validParams)?.flat(),
    };
  }, [excludeFilterTypes, filters]);

  useLayoutEffect(() => {
    if (quickViewProps?.type === QUICK_VIEW_TYPES.FILTERS && Object.keys(commonFilterParams)?.length > 0) {
      showQuickView({
        type: QUICK_VIEW_TYPES.FILTERS,
        data: {
          ...quickViewProps?.data,
          commonFilterParams,
        },
      });
    }
  }, [commonFilterParams]);

  const onResetFilters = useCallback(
    e => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }

      resetFilters({ entityType, entity });

      navigate.scrollTo();
    },
    [navigate, resetFilters, entityType, entity],
  );

  const showQuickViewFilters = useCallback(
    ({ type, selectedSubFilterType }) => {
      showQuickView({
        type: QUICK_VIEW_TYPES.FILTERS,
        data: {
          types,
          mainPath,
          commonFilterParams,
          disable,
          disabledTypes,
          clearExcludedTypes,
          upcoming,
          defaultExpandedFilter: type || filterGroup[0][0],
          defaultSubFilterType: selectedSubFilterType,
          openInQuickView: false,
          trackingData,
        },
      });
    },
    [
      clearExcludedTypes,
      commonFilterParams,
      disable,
      disabledTypes,
      filterGroup,
      mainPath,
      showQuickView,
      trackingData,
      types,
      upcoming,
    ],
  );

  const onExpandFilter = useCallback(
    ({ type, anchor, trackingData: customTrackingData, selectedSubFilterType }) => {
      if (layout !== LAYOUT.VERTICAL && openInQuickView) {
        if (disable) {
          if (!isLoggedIn) {
            setIsLoginDialog({ isOpen: true });
          } else {
            showPageAccessPortal();
          }

          return;
        }

        showQuickViewFilters({ type, selectedSubFilterType });
      } else {
        setSubFilterType(selectedSubFilterType);
        setFilterAnchor(anchor);
        setExpandedFilter(type || types[0]);
      }

      track.click({
        ...(customTrackingData || trackingData),
        subComponent: SUB_COMPONENTS.FILTER_CHIP,
        meta: { filterTab: type || types[0] },
      });
    },
    [
      layout,
      openInQuickView,
      track,
      trackingData,
      types,
      disable,
      showQuickViewFilters,
      isLoggedIn,
      hasCastingToolAccess,
      setIsLoginDialog,
      showPageAccessPortal,
    ],
  );

  const onCloseFilter = useCallback(() => {
    setFilterAnchor(null);
    setExpandedFilter(null);
  }, []);

  const onApplyFilters = useCallback(
    ({ options, isClearAll }) => {
      // NOTE: added this if condition only for testing profession filters, need to remove this
      let filterSubPath;
      let filtersParams = {};

      if (isClearAll) {
        filtersParams = pick(filters, excludeFilterTypes);
      } else {
        filtersParams = transformAppliedFilters({
          options,
          filters: pick(filters, excludeFilterTypes),
          subFilterType,
          selectedFilterType: expandedFilter,
        });
      }

      const data = {
        entityType,
        entity,
        mainPath,
        subPath: filterSubPath,
        filters: filtersParams,
      };

      applyFilters(data);
      track.click({
        ...trackingData,
        subComponent: trackingData?.subComponent
          ? `${trackingData.subComponent} / ${SUB_COMPONENTS.SUBMIT_CTA}`
          : SUB_COMPONENTS.SUBMIT_CTA,
        meta: { filters: data.filters },
      });
    },
    [
      filters,
      subFilterType,
      expandedFilter,
      mainPath,
      entityType,
      entity,
      applyFilters,
      track,
      trackingData,
      excludeFilterTypes,
    ],
  );

  const onUpdateFilters = useCallback(
    ({ selectedOptions, isClearAll = false }) => {
      if (layout === LAYOUT.QUICK_VIEW) {
        if (!isClearAll) {
          onCloseQuickView();
        }
      } else {
        onCloseFilter();
      }

      if (selectedOptions) {
        onApplyFilters({ options: selectedOptions, isClearAll });
      }
    },
    [layout, onApplyFilters, onCloseFilter, onCloseQuickView],
  );

  const onAccessArchives = useCallback(() => {
    onCloseFilter();
    openArchiveAccessModal();
  }, [onCloseFilter, openArchiveAccessModal]);

  if (layout === LAYOUT.QUICK_VIEW) {
    return (
      <FilterModalContent
        stats={stats}
        subFilterType={subFilterType}
        appliedFilters={baseAppliedFilters}
        expandedFilter={expandedFilter}
        onClose={onCloseQuickView}
        onAccessArchives={onAccessArchives}
        onUpdateFilters={onUpdateFilters}
        onExpandFilter={onExpandFilter}
        upcoming={upcoming}
        disable={disable}
        disabledTypes={disabledTypes}
        filterGroup={filterGroup}
        hasFiltersApplied={hasFiltersApplied}
        fullHeight
        isSticky
        trackingData={trackingData}
      />
    );
  }

  if (layout === LAYOUT.BUTTON_GROUP_DETAILED) {
    return (
      <FilterGroupButton
        types={types}
        disable={disable}
        facetsApiFilters={facetsApiFilters}
        appliedFilters={baseAppliedFilters}
        onExpandFilter={onExpandFilter}
        layout={layout}
        customGroups={customGroup}
        styles={styles}
        ignoreSmallScreenGroup={ignoreSmallScreenGroup}
        truncateInOneLine={truncateInOneLine}
        viewType={viewType}
      />
    );
  }

  if (layout === LAYOUT.BUTTON) {
    return (
      <LinkButton
        variant="grey"
        shape="rectangle"
        styles={{
          root: classnames(classes.filterBtn, { [classes.filterBtn_disabled]: disable }),
        }}
        size="medium"
        onClick={() => {
          onExpandFilter({ type: filterGroup[0][0] });
        }}
        leftIcon={<SpriteIcon icon="filter_list" size={14} className={classes.filterBtn__icon} />}
        disableHover
        trackingData={{
          ...trackingData,
          subComponent: SUB_COMPONENTS.FILTER_CTA,
        }}
      >
        {t(`${TP}.m_ALL`)} {t(`${TP}.FILTERS`)}
        {validFilters?.length > 0 && (
          <Typography variant="span" size={12} weight="bold" className={classes.filterBtn__count} color="white">
            {validFilters.length}
          </Typography>
        )}
      </LinkButton>
    );
  }

  if (layout === LAYOUT.TEXT) {
    return (
      <TextButton
        size="medium"
        onClick={() => {
          onExpandFilter({ type: filterGroup[0][0] });
        }}
        leftIcon={<SpriteIcon icon="filter_list" size={14} className={classes.filterBtn__icon} />}
        trackingData={{
          ...trackingData,
          subComponent: SUB_COMPONENTS.FILTER_CTA,
        }}
      >
        {t(`${TP}.FILTERS`)}
      </TextButton>
    );
  }

  if (layout === LAYOUT.ICON) {
    const showWorksRoles = Object.keys(baseAppliedFilters).some(
      key => key.includes('expression') && !baseAppliedFilters?.expression?.includes('|'),
    );
    return (
      <LinkButton
        variant="grey"
        styles={{ root: classes.filterBtn__onlyIcon }}
        size="medium"
        disableHover
        onClick={() => {
          onExpandFilter({ type: showWorksRoles ? ' ' : types[0] });
        }}
        trackingData={{
          ...trackingData,
          subComponent: SUB_COMPONENTS.FILTER_CTA,
        }}
      >
        <SpriteIcon icon="filter_list" size={14} />
      </LinkButton>
    );
  }

  return (
    <>
      <div className={classnames(classes.root, { [classes.inline]: isInlineLayout, [styles.root]: !!styles.root })}>
        {!isMobile && (
          <div
            className={classnames(classes.header, {
              [classes.inlineHeader]: isInlineLayout,
              [classes.headerFilterApplied]: hasFiltersApplied,
              [classes.disabled]: disable,
            })}
            {...(isInlineLayout && { onClick: () => onExpandFilter({ type: types[0] }) })}
          >
            <div className={classes.header__icon}>
              <SpriteIcon icon="filter_list" size={isInlineLayout ? 12 : 14} />
            </div>
            <div className={classes.header__label}>
              <Typography
                variant="h2"
                weight={isInlineLayout ? 'regular' : 'medium'}
                size={isInlineLayout ? 12 : 14}
                className={classnames(classes.header__label_text, {
                  [classes.header__label_textApplied]: validFilters?.length > 0,
                })}
              >
                {t(`${TP}.m_ALL`)} {t(`${TP}.FILTERS`)}
              </Typography>
            </div>
            {validFilters?.length > 0 && (
              <Typography size={12} className={classes.chip__applied} color="white">
                {validFilters.length}
              </Typography>
            )}
          </div>
        )}
        <RenderFacets
          filterGroup={filterGroup}
          stats={stats}
          subFilterType={subFilterType}
          commonFilterParams={baseAppliedFilters}
          onExpandFilter={onExpandFilter}
          inline={isInlineLayout}
          scrollable={scrollable}
          disable={disable}
          expandedFilter={expandedFilter}
          disabledTypes={disabledTypes}
          upcoming={upcoming}
          hideFilterCounts={isStubOverviewTab}
          trackingData={trackingData}
        />

        {expandedFilter && layout === LAYOUT.VERTICAL && (
          <Popover
            anchorEl={filterAnchor}
            container={filterAnchor}
            onClose={onCloseFilter}
            styles={{
              root: classes.filterPopover,
            }}
            anchorOrigin={{
              horizontal: 'right',
            }}
            transformOrigin={{
              horizontal: 'left',
            }}
            transition
          >
            <FilterOptions
              stats={stats}
              selectedFilterType={expandedFilter}
              total={stats?.[expandedFilter]}
              filterParams={baseAppliedFilters}
              onUpdateFilters={onUpdateFilters}
              onAccessArchives={onAccessArchives}
              upcoming={upcoming}
              trackingData={trackingData}
            />
          </Popover>
        )}
      </div>

      {expandedFilter && layout !== LAYOUT.VERTICAL && (
        <Modal
          onClose={onCloseFilter}
          styles={{
            modalContainer: classes.filterModal,
            drawerPaper: classes.filterDrawerPaper,
            content: classes.filterDrawerContent,
          }}
          header={
            <FilterModalHeader
              mainPath={mainPath}
              upcoming={upcoming}
              hasFiltersApplied={hasFiltersApplied}
              onResetFilters={onResetFilters}
              onClose={onCloseFilter}
              trackingData={trackingData}
            />
          }
          isOpen
          allowMobileDrawer
        >
          <FilterModalContent
            stats={stats}
            appliedFilters={baseAppliedFilters}
            expandedFilter={expandedFilter}
            onAccessArchives={onAccessArchives}
            onUpdateFilters={onUpdateFilters}
            onExpandFilter={onExpandFilter}
            upcoming={upcoming}
            disable={disable}
            disabledTypes={disabledTypes}
            trackingData={trackingData}
            onClose={onCloseQuickView}
            hasFiltersApplied={hasFiltersApplied}
          />
        </Modal>
      )}
    </>
  );
}

export function FilterSidebar(props) {
  return (
    <FilterFacets {...props} openInQuickView={false} layout={LAYOUT.QUICK_VIEW} trackingData={filterTrackingData} />
  );
}

export default FilterFacets;
